import { useState, RefObject, useEffect, useCallback } from 'react';

import ExportIcon from '~/shared/assets/icons/export.svg';
import { ReportSelectionOption, Account } from '~/shared/utils/types';
import ChartFilterDropdown from '~/shared/components/ChartFilterDropdown';
import Button from '~/shared/components/ButtonsV2';
import VolumeViewDropdown from '~/shared/components/filters/VolumeViewDropdown';
import { LineChartHandle } from '~/shared/components/LineChart/LineChart';
import { useExportState } from '~/shared/context/ExportStateContext';
import { getVolumeViewPreset, VolumeViewPresetId } from '~/shared/utils/volumeViewPresets';
import {
  useAppliedVolumeFilters,
  useVolumeFilterUrlParams,
} from '~/shared/components/filters/volumeFilters';

import { CUSTOM_DATES_TITLE, dateFilterItems } from '../common/dateFilterMenuItems';
import ReportsExportModal from '../ReportsPage/components/ReportsExportModal';

import ReportsFiltersSidePanel from './FiltersSidePanel/ReportsFiltersSidePanel';

interface ReportsFilterSectionProps {
  activeDateFilterTitle: string | null;
  currentBaseFilters: ReportSelectionOption[];
  onDateFilterChange: (filter: {
    title: string;
    duration?: { start: Duration; end?: Duration };
  }) => void;
  onBaseFiltersChange: (newFilters: ReportSelectionOption[]) => void;
  setPageOffset: (offset: string) => void;
  utilizationChartRef: RefObject<LineChartHandle | null>;
  usageChartRef: RefObject<LineChartHandle | null>;
  tableQueryUrl: string;
  tableCountUrl: string;
  chartQueryUrl: string;
  totalCount: number | undefined;
  isOrgView?: boolean;
  accounts?: Account[];
  activeOrgId?: string;
  presetCounts: Partial<Record<VolumeViewPresetId, number>>;
  presetCountsLoading: boolean;
}

export default function ReportsFilterSection({
  activeDateFilterTitle,
  onDateFilterChange,
  setPageOffset,
  utilizationChartRef,
  usageChartRef,
  tableQueryUrl,
  tableCountUrl,
  chartQueryUrl,
  totalCount,
  isOrgView = false,
  accounts = [],
  activeOrgId,
  presetCounts,
  presetCountsLoading,
}: ReportsFilterSectionProps) {
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const { isExporting } = useExportState();
  const reportsExporting = isExporting('reports');
  const {
    activeFilterCount,
    currentBaseFilters,
    needsAutoscalingUrlMigration,
    ...applied
  } = useAppliedVolumeFilters();
  const { appliedAutoscaleState, appliedEligibility } = applied;

  const { applyAutoscalingFiltersOnly, resetFilters } = useVolumeFilterUrlParams();

  useEffect(() => {
    if (!needsAutoscalingUrlMigration) {
      return;
    }

    applyAutoscalingFiltersOnly(appliedAutoscaleState, appliedEligibility);
  }, [
    needsAutoscalingUrlMigration,
    appliedAutoscaleState,
    appliedEligibility,
    applyAutoscalingFiltersOnly,
  ]);

  const activeDateFilterSelectedItem: {
    title: string;
    duration?: { start: Duration; end?: Duration };
  } = (() => {
    if (activeDateFilterTitle && activeDateFilterTitle.includes(CUSTOM_DATES_TITLE)) {
      return { title: activeDateFilterTitle };
    }
    const foundItem = dateFilterItems.find((item) => item.title === activeDateFilterTitle);
    return (
      foundItem ||
      dateFilterItems.find((item) => item.title === 'Last 3 hours') ||
      dateFilterItems[0] || { title: 'Error: No Date Filter' }
    );
  })();

  const handleResetFilters = useCallback(() => {
    setPageOffset('0');
    resetFilters();
    setSidePanelOpen(false);
  }, [resetFilters, setPageOffset]);

  const handleSelectVolumeViewPreset = useCallback(
    (presetId: VolumeViewPresetId) => {
      const preset = getVolumeViewPreset(presetId);
      setPageOffset('0');
      applyAutoscalingFiltersOnly(preset.stateIds, preset.eligibilityIds);
    },
    [applyAutoscalingFiltersOnly, setPageOffset]
  );

  return (
    <div className="px-[30px] py-[20px] flex justify-between gap-4 ">
      <VolumeViewDropdown
        appliedAutoscaleState={appliedAutoscaleState}
        appliedEligibility={appliedEligibility}
        onSelectPreset={handleSelectVolumeViewPreset}
        presetCounts={presetCounts}
        isLoading={presetCountsLoading}
      />
      <div className="ml-auto flex gap-2 z-30 items-center">
        <ChartFilterDropdown
          align="top-right"
          items={dateFilterItems}
          onSelect={onDateFilterChange}
          selectedItem={activeDateFilterSelectedItem}
        />
        <ReportsFiltersSidePanel
          open={sidePanelOpen}
          onOpenChange={setSidePanelOpen}
          onResetFilters={handleResetFilters}
          applied={applied}
          activeFilterCount={activeFilterCount}
          setSidePanelOpen={setSidePanelOpen}
          isSidePanelOpen={sidePanelOpen}
          isOrgView={isOrgView}
          accounts={accounts}
          activeOrgId={activeOrgId}
        />
        <Button
          variant="third"
          onClick={() => setIsExportModalOpen(true)}
          className="w-10"
          size="md-responsive"
          loading={reportsExporting}
          disabled={reportsExporting}
          aria-label="Export reports data"
          icon={<ExportIcon className="size-5" />}
        />
      </div>

      <ReportsExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        activeFilters={currentBaseFilters}
        utilizationChartRef={utilizationChartRef}
        usageChartRef={usageChartRef}
        tableQueryUrl={tableQueryUrl}
        tableCountUrl={tableCountUrl}
        chartQueryUrl={chartQueryUrl}
        activeDateFilterTitle={activeDateFilterTitle}
      />
    </div>
  );
}
